import React from "react";
import { getRouteMeta, SITE } from "./routeMeta";

/**
 * Per-page SEO block. Emits <title>, meta description, canonical, Open Graph,
 * Twitter Card and JSON-LD (WebPage / Article) schema for the current pathname.
 *
 * Uses React 19's native metadata hoisting — any <title>, <meta>, <link>,
 * <script> element rendered anywhere in the tree is automatically hoisted
 * into <head>, and React de-dupes on subsequent renders.
 * See https://react.dev/reference/react-dom/components/meta
 *
 * Usage:  <SeoHead path="/dashboard" />
 *
 * The metadata source of truth is `src/seo/routeMeta.js` — shared with the
 * build-time static-shell generator so client-side and server-rendered HTML
 * stay in sync.
 */
export const SeoHead = ({ path }) => {
  const meta = getRouteMeta(path);
  const isArticle = meta.schemaType === "Article";
  const today = new Date().toISOString().slice(0, 10);

  const schema = isArticle
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: meta.h1,
        description: meta.description,
        url: meta.canonical,
        image: SITE.OG_IMAGE,
        publisher: {
          "@type": "Organization",
          name: SITE.SITE_NAME,
          url: SITE.SITE_URL,
        },
        datePublished: today,
        dateModified: today,
        mainEntityOfPage: { "@type": "WebPage", "@id": meta.canonical },
      }
    : {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: meta.h1 || meta.title,
        description: meta.description,
        url: meta.canonical,
        isPartOf: {
          "@type": "WebSite",
          name: SITE.SITE_NAME,
          url: SITE.SITE_URL,
        },
      };

  return (
    <>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={meta.canonical} />
      {meta.noindex && <meta name="robots" content="noindex,follow" />}

      {/* Open Graph */}
      <meta property="og:type" content={isArticle ? "article" : "website"} />
      <meta property="og:site_name" content={SITE.SITE_NAME} />
      <meta property="og:url" content={meta.canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={SITE.OG_IMAGE} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={SITE.OG_IMAGE} />

      {/* JSON-LD structured data */}
      <script
        type="application/ld+json"
        // React 19 renders script children as text nodes; use dangerouslySetInnerHTML
        // so the JSON stays exactly as-is (no escaping surprises).
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    </>
  );
};

export default SeoHead;
